import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Video, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { reviewFormatLabels, reviewLanguageLabels } from '@/types/database';

interface SuggestReviewButtonProps {
  brandId?: string;
  modelId?: string;
  variantId?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  showLabel?: boolean;
}

export function SuggestReviewButton({
  brandId: lockedBrandId,
  modelId: lockedModelId,
  variantId: lockedVariantId,
  brandName = '',
  modelName = '',
  variantName = '',
  showLabel = true,
}: SuggestReviewButtonProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const carLocked = !!(lockedBrandId && lockedModelId);

  const [brandId, setBrandId] = useState<string>(lockedBrandId ?? '');
  const [modelId, setModelId] = useState<string>(lockedModelId ?? '');
  const [variantId, setVariantId] = useState<string>(lockedVariantId ?? '');

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [publisher, setPublisher] = useState('');
  const [format, setFormat] = useState<string>('Article');
  const [language, setLanguage] = useState<string>('DA');
  const [publishDate, setPublishDate] = useState('');
  const [summary, setSummary] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (open) {
      setBrandId(lockedBrandId ?? '');
      setModelId(lockedModelId ?? '');
      setVariantId(lockedVariantId ?? '');
    }
  }, [open, lockedBrandId, lockedModelId, lockedVariantId]);

  const { data: brands = [] } = useQuery({
    queryKey: ['suggest-review-brands'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: open && !carLocked,
  });

  const { data: models = [] } = useQuery({
    queryKey: ['suggest-review-models', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('models')
        .select('id, name')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: open && !carLocked && !!brandId,
  });

  const { data: variants = [] } = useQuery({
    queryKey: ['suggest-review-variants', modelId],
    queryFn: async () => {
      if (!modelId) return [];
      const { data, error } = await supabase
        .from('variants')
        .select('id, name')
        .eq('model_id', modelId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
    enabled: open && !carLocked && !!modelId,
  });

  const resetForm = () => {
    setTitle('');
    setUrl('');
    setPublisher('');
    setFormat('Article');
    setLanguage('DA');
    setPublishDate('');
    setSummary('');
    setEmail('');
    if (!carLocked) {
      setBrandId('');
      setModelId('');
      setVariantId('');
    }
  };

  const selectedBrandName =
    lockedBrandId ? brandName : brands.find(b => b.id === brandId)?.name ?? '';
  const selectedModelName =
    lockedModelId ? modelName : models.find(m => m.id === modelId)?.name ?? '';
  const selectedVariantName =
    lockedVariantId ? variantName : variants.find(v => v.id === variantId)?.name ?? '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !url.trim() || !publisher.trim() || !brandId || !modelId) {
      toast.error('Udfyld venligst alle påkrævede felter');
      return;
    }

    try {
      new URL(url);
    } catch {
      toast.error('Angiv venligst en gyldig URL');
      return;
    }

    setIsSubmitting(true);

    const payload = {
      title: title.trim(),
      url: url.trim(),
      publisher: publisher.trim(),
      format: format as 'Video' | 'Article',
      language: language as 'DA' | 'EN' | 'Other',
      publish_date: publishDate || null,
      summary: summary.trim() || null,
      brand_id: brandId,
      model_id: modelId,
      variant_id: variantId || null,
      brand_name: selectedBrandName,
      model_name: selectedModelName,
      variant_name: selectedVariantName || null,
      submitter_email: email.trim() || null,
      status: 'pending' as const,
    };
    const { submitForm } = await import('@/lib/submission');
    const res = await submitForm({
      formName: 'suggest_review',
      table: 'review_submissions',
      payload,
      email: payload.submitter_email,
      perform: () => supabase.from('review_submissions').insert(payload),
    });
    if (res.ok) {
      toast.success('Tak! Vi gennemgår din anmeldelse.');
      resetForm();
      setOpen(false);
    } else {
      toast.error(res.userMessage ?? 'Der opstod en fejl. Prøv venligst igen.');
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="accent" className="gap-2 whitespace-nowrap shrink-0 shadow-sm font-semibold">
          <Video className="w-4 h-4" />
          {showLabel && <span>Foreslå anmeldelse</span>}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Foreslå en anmeldelse</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {carLocked ? (
            <div className="p-3 rounded-md bg-muted text-sm">
              <p className="text-xs text-muted-foreground mb-1">Anmeldelse af</p>
              <p className="font-medium">
                {brandName} {modelName}
                {variantName ? <span className="text-muted-foreground"> · {variantName}</span> : null}
              </p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mærke *</Label>
                  <Select
                    value={brandId}
                    onValueChange={(v) => {
                      setBrandId(v);
                      setModelId('');
                      setVariantId('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Vælg mærke..." />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Model *</Label>
                  <Select
                    value={modelId}
                    onValueChange={(v) => {
                      setModelId(v);
                      setVariantId('');
                    }}
                    disabled={!brandId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={brandId ? 'Vælg model...' : 'Vælg mærke først'} />
                    </SelectTrigger>
                    <SelectContent>
                      {models.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Variant (valgfrit)</Label>
                <Select
                  value={variantId || '__none__'}
                  onValueChange={(v) => setVariantId(v === '__none__' ? '' : v)}
                  disabled={!modelId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={modelId ? 'Vælg variant...' : 'Vælg model først'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Hele modellen</SelectItem>
                    {variants.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mangler bilen?{' '}
                  <a href="/bidrag" className="underline">Foreslå den her</a>.
                </p>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">Titel på anmeldelse *</Label>
            <Input
              id="title"
              placeholder="f.eks. FDMs test af Tesla Model 3"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="url">Link til anmeldelse *</Label>
            <Input
              id="url"
              type="url"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="publisher">Udgiver *</Label>
            <Input
              id="publisher"
              placeholder="f.eks. FDM, Bilmagasinet, YouTube-kanal..."
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="format">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(reviewFormatLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="language">Sprog</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(reviewLanguageLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="publishDate">Udgivelsesdato (valgfrit)</Label>
            <Input
              id="publishDate"
              type="date"
              value={publishDate}
              onChange={(e) => setPublishDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="summary">Kort beskrivelse (valgfrit)</Label>
            <Textarea
              id="summary"
              placeholder="En kort beskrivelse af anmeldelsen..."
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Din email (valgfrit)</Label>
            <Input
              id="email"
              type="email"
              placeholder="din@email.dk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Hvis du vil have besked når anmeldelsen er godkendt
            </p>
          </div>

          <Button type="submit" className="w-full gap-2" disabled={isSubmitting}>
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Sender...' : 'Send forslag'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
